import { useCallback } from 'react'
import { RowActionButtons } from './components/RowActionButtons'
import { CIMSTableProps } from '../../../types'
import { ColumnsType } from 'antd/lib/table'
import {
  useCheckPermissions,
  useCheckPermissionsMap,
} from 'hooks/useCheckPermission'
import { getDeepestString } from 'utils/getReactNodeLength'
import { useProjectCustomFieldsContext } from '../../../../CustomFields/ProjectCustomFieldsContext'
import HelpIcon from '../../../../HelpIcon'

export function useActionsColumnFn<T extends object, U>({
  actions,
  records,
  rowKey,
}: Pick<CIMSTableProps<T, U>, 'actions' | 'records' | 'rowKey'>) {
  const { editorOnSome } = useProjectCustomFieldsContext()
  const canRemove = useCheckPermissions(records)
  const canEdit = useCheckPermissions(records)
  const [, someRowsAllowed] = useCheckPermissionsMap(
    (actions?.row?.custom ?? []).map((custom, key) => {
      if (custom.type === 'externalLink') return { key, permission: true }
      const { permission } = custom
      if (typeof permission === 'function')
        return { key, permission, record: records ?? [] }
      return { key, permission }
    })
  )

  return useCallback<(columns: ColumnsType<T>) => ColumnsType<T>>(
    (columns) => {
      const canAct =
        canRemove(actions?.row?.remove?.permission) ||
        canEdit(actions?.row?.edit?.permission) ||
        editorOnSome ||
        someRowsAllowed
      if (actions?.row && canAct) {
        const longestCustom = (actions.row.custom ?? []).reduce(
          (max, custom) => {
            /**
             * when undefined, we _want_ to show it
             */
            let visible = true
            const nodeOrFn =
              custom.type === 'externalLink' ? custom.label : custom.text
            const text =
              typeof nodeOrFn === 'function'
                ? (records ?? []).reduce((text, record) => {
                    const computed = getDeepestString(nodeOrFn(record))
                    if (text.length < computed.length) return computed
                    return text
                  }, '')
                : getDeepestString(nodeOrFn)
            if (custom.type === 'externalLink') visible = true
            else {
              const { hidden } = custom
              if (typeof hidden === 'function')
                visible = !records?.every((record) => hidden(record))
              if (typeof hidden === 'boolean') visible = !hidden
            }
            if (visible) return Math.max(max, text.length)
            return max
          },
          0
        )
        const width = longestCustom > 0 ? `${longestCustom + 5}ch` : 100
        columns.push({
          title: <HelpIcon text={actions.row.help}>Actions</HelpIcon>,
          width,
          fixed: 'right',
          key: 'actions',
          render: (_, record) => {
            const key =
              typeof rowKey === 'function'
                ? rowKey(record)
                : String(record[rowKey])
            return (
              <RowActionButtons
                record={record}
                key={`row-action-buttons-${key}`}
              />
            )
          },
        })
      }
      return columns
    },
    [
      actions?.row,
      canEdit,
      canRemove,
      editorOnSome,
      records,
      rowKey,
      someRowsAllowed,
    ]
  )
}
